import * as React from 'react';
import { ReactComponent as TaxIcon } from 'assets/icons/icon_tax.svg';
import { IconButton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LrDialog from 'components/LrDialog';
import { useEffect, useState } from 'react';
import { TaxesAndFees } from 'types/types.type';
import { fetchTaxesAndFeesForQuote } from '../../../../services/quotes';
import { LabeledData } from '../../../Input';
import { formatPercent } from '../../../../helpers/formatters';

interface Props {
    stateCode: string;
    timeout?: number; // timeout in ms after which the update callback will be called with empty data
    onTaxesAndFeesUpdate: (taxData: TaxData) => void;
}

export interface TaxData {
    totalTaxRate?: number;
    totalFeesRate?: number;
    feesTaxed?: boolean;
    roundingPrecisionDigits?: number;
}

const defaultProps = {
    timeout: 4000,
};

const TaxAndFeesInfoDialog: React.FunctionComponent<Props> = (props) => {
    const { stateCode, onTaxesAndFeesUpdate, timeout } = { ...props };
    const [isOpen, setIsOpen] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [taxesAndFees, setTaxesAndFees] = useState<TaxesAndFees | undefined>(undefined);
    const loadingTimeout = React.useRef<number | null>(null);

    const getTaxesAndFees = async () => {
        try {
            const data = await fetchTaxesAndFeesForQuote(stateCode);
            if (data) {
                setHasData(true);
                setTaxesAndFees(data);
            }
        } catch (e) {
            setTaxesAndFees({} as TaxesAndFees);
        }
    };

    useEffect(() => {
        if (stateCode) {
            getTaxesAndFees();
        }
    }, [stateCode]);
    useEffect(() => {
        if (taxesAndFees) {
            if (loadingTimeout.current != null) {
                clearTimeout(loadingTimeout.current);
                loadingTimeout.current = null;
            }
            onTaxesAndFeesUpdate?.({
                totalTaxRate: taxesAndFees.stateTaxRate,
                totalFeesRate: taxesAndFees.stateStampingFeeRate,
                feesTaxed: taxesAndFees.feesTaxed,
                roundingPrecisionDigits: taxesAndFees.roundingPrecisionDigits,
            });
        }
    }, [taxesAndFees]);

    useEffect(() => {
        if (loadingTimeout.current === null) {
            loadingTimeout.current = setTimeout(() => {
                onTaxesAndFeesUpdate?.({});
                loadingTimeout.current = null;
            }, timeout);
        }
    }, []);

    const onCancelorOk = () => {
        setIsOpen(false);
    };

    const renderInfo = () => (
        <Grid container direction={'column'}>
            <Grid>
                <LabeledData title={'Coverage Type'} value={taxesAndFees?.coverageType} />
            </Grid>
            <Grid>
                <LabeledData title={'Tax Rate'} value={formatPercent(taxesAndFees?.taxRate || 0, 3)} />
            </Grid>
            <Grid>
                <LabeledData title={'Stamping Fee Rate'} value={formatPercent(taxesAndFees?.stampingFeeRate || 0, 3)} />
            </Grid>
            <Grid>
                <LabeledData
                    title={'Fire Marshal Tax Rate'}
                    value={formatPercent(taxesAndFees?.firemarshalTaxRate || 0, 3)}
                />
            </Grid>
            <Grid>
                <LabeledData title={'Muni Fee Rate'} value={formatPercent(taxesAndFees?.municipalityFeeRate || 0, 3)} />
            </Grid>
        </Grid>
    );

    return (
        <>
            <IconButton disabled={!hasData} onClick={() => setIsOpen(true)} size="large">
                <TaxIcon fill={'#818184'} stroke={'#818184'} />
            </IconButton>
            <LrDialog
                isOpen={isOpen}
                title="Taxes And Fees"
                submitButtonText={'OK'}
                onSubmit={onCancelorOk}
                onCancel={onCancelorOk}
            >
                {renderInfo()}
            </LrDialog>
        </>
    );
};

TaxAndFeesInfoDialog.defaultProps = defaultProps;
export default TaxAndFeesInfoDialog;
